import type { AxiosResponse } from 'axios';

import { useApiResponse } from '@/composables/useApiResponse';
import { useLaunchToast } from '@/composables/useToast';
import {
    DEFAULT_PAGE_SIZE,
    HTTP_STATUS_CODE_NOT_FOUND,
    ONE_MINUTE_IN_MS,
    QUERY_CREDIT_NOTE_LIST,
    QUERY_ORDER,
    QUERY_ORDERS,
    QUERY_PROCESSED_ORDERS,
    QUERY_SET_ORDER_CLIENT,
    QUERY_SET_ORDER_IS_CONSTRUCTION_SERVICE,
    QUERY_TRANSFER,
} from '@/constants';
import {
    type ConstructionServiceRequest,
    type ConstructionServiceTransferRequest,
    type GetOrderIdsResponse,
    type GetOrderResponse,
    type GetProcessedOrdersRequest,
    type OrderIsEditedNotification,
    type OrderSetClientRequest,
    type ProcessedOrderResponse,
    type ProcessedOrderResponsePagedListResponse,
    ProcessedOrderSorting,
} from '@/generated';
import useAuthStore from '@/stores/useAuthStore';
import useDialogStore from '@/stores/useDialogStore';
import useLocationsStore from '@/stores/useLocationsStore';
import useOrderStore from '@/stores/useOrderStore';
import { invalidateQueryKeys } from '@/utils/queryInvalidation';
import { QueryClient, useMutation, useQuery, useQueryClient } from '@tanstack/vue-query';
import { storeToRefs } from 'pinia';
import type { ComputedRef, Ref } from 'vue';
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';

import { getUserIds, getUsersQueryOptions } from './admin';
import { creditNoteApi, orderApi } from './generatedApi';

const UNKNOWN_ERROR_TRANSLATION_KEY = 'actions.errors.unknown';

export const getEditedOrderIdsFromNotification = (notification: unknown): number[] => {
    if (!notification || typeof notification !== 'object') {
        return [];
    }

    const payload = notification as OrderIsEditedNotification & { orderId?: number };
    if (Array.isArray(payload.orderIds)) {
        return payload.orderIds.filter((id): id is number => typeof id === 'number');
    }

    if (typeof payload.orderId === 'number') {
        return [payload.orderId];
    }

    return [];
};

type ProcessedOrdersQueryData = {
    data?: ProcessedOrderResponse[];
    totalCount?: number;
};

export const removeEditedOrdersFromProcessedOrdersCache = (
    queryClient: QueryClient,
    notification: unknown
) => {
    const editedOrderIds = getEditedOrderIdsFromNotification(notification);
    if (!editedOrderIds.length) {
        return;
    }

    const editedOrderIdSet = new Set(editedOrderIds);
    queryClient.setQueriesData<ProcessedOrdersQueryData>(
        { queryKey: [QUERY_PROCESSED_ORDERS] },
        oldData => {
            if (!oldData?.data?.length) {
                return oldData;
            }

            const filteredData = oldData.data.filter(order => {
                const id = order?.orderShortInfo?.id;
                return typeof id !== 'number' || !editedOrderIdSet.has(id);
            });

            if (filteredData.length === oldData.data.length) {
                return oldData;
            }

            const removedCount = oldData.data.length - filteredData.length;
            return {
                ...oldData,
                data: filteredData,
                totalCount: Math.max(0, (oldData.totalCount ?? oldData.data.length) - removedCount),
            };
        }
    );
};

export const removeOrdersToProcessFromCache = (queryClient: QueryClient, orderIds: number[]) => {
    if (!orderIds.length) {
        return;
    }

    const editedOrderIdSet = new Set(orderIds);
    queryClient.setQueriesData<GetOrderResponse[]>({ queryKey: [QUERY_ORDERS] }, oldData => {
        if (!oldData?.length) {
            return oldData;
        }

        const filteredData = oldData.filter(order => {
            const id = order?.order?.id;
            return typeof id !== 'number' || !editedOrderIdSet.has(id);
        });

        return filteredData.length === oldData.length ? oldData : filteredData;
    });
};

export const applyOrderEditedNotificationToCache = async (
    queryClient: QueryClient,
    notification: unknown,
    options?: {
        invalidateProcessedOrders?: boolean;
        removeOrdersToProcess?: boolean;
    }
): Promise<number[]> => {
    const editedOrderIds = getEditedOrderIdsFromNotification(notification);
    if (!editedOrderIds.length) {
        return [];
    }

    if (options?.removeOrdersToProcess !== false) {
        removeOrdersToProcessFromCache(queryClient, editedOrderIds);
    }
    removeEditedOrdersFromProcessedOrdersCache(queryClient, notification);

    if (options?.invalidateProcessedOrders) {
        void invalidateQueryKeys(queryClient, [[QUERY_PROCESSED_ORDERS]]);
    }

    return editedOrderIds;
};

export const updateOrderInOrderQueryCache = (
    queryClient: QueryClient,
    orderId: number,
    patch: Partial<NonNullable<GetOrderResponse['order']>>
) => {
    queryClient.setQueryData<GetOrderResponse>([QUERY_ORDER, orderId], order => {
        if (!order?.order) {
            return order;
        }

        return {
            ...order,
            order: {
                ...order.order,
                ...patch,
            },
        };
    });
};

export const loadOrder = async (
    orderId: number,
    queryClient: QueryClient
): Promise<GetOrderResponse> => {
    const apiResponseCode = useApiResponse();
    try {
        const response = await orderApi.apiV1OrderOrderIdGet(orderId);

        if (!apiResponseCode(response.status).ok) {
            throw new Error(`Could not load order with ID ${orderId}. ${response.statusText}`, {
                cause: response.status,
            });
        }

        const order = response.data;
        if (!order?.order?.id) {
            throw new Error(`Could not find order with ID ${orderId}. ${response.statusText}`, {
                cause: HTTP_STATUS_CODE_NOT_FOUND,
            });
        }

        const { selectedLocationIds } = storeToRefs(useLocationsStore());

        // Update single order in TSQ-order-array
        queryClient.setQueryData(
            [QUERY_ORDERS, selectedLocationIds],
            (old: GetOrderResponse[] | undefined) => {
                if (!old?.length) {
                    return old;
                }

                const containedIndex = old.findIndex(o => o?.order?.id === order?.order?.id);

                if (containedIndex >= 0) {
                    const newArray = [...old];
                    newArray.splice(containedIndex, 1, order);
                    return newArray;
                }

                return old;
            }
        );

        return order;
    } catch (e) {
        console.error(e);
        throw e;
    }
};

/** Loads one full order */
export const useGetOrder = () => {
    const { currentOrder } = storeToRefs(useOrderStore());
    const { isAuthenticated } = storeToRefs(useAuthStore());
    const queryClient = useQueryClient();

    return useQuery({
        queryKey: [QUERY_ORDER, computed(() => currentOrder.value?.id)],
        queryFn: () => loadOrder(currentOrder.value?.id || 0, queryClient),
        /* Having only an order with an order id but without a partner id is the key indicator
         * that an order must be fully loaded.*/
        enabled: computed(
            () =>
                isAuthenticated.value &&
                (currentOrder.value?.id || 0) > 0 &&
                !currentOrder.value?.partner?.id
        ),
    });
};

/** Loads only order ids */
export const useGetOrderList = () => {
    const { selectedLocationIds, locationInfo } = storeToRefs(useLocationsStore());
    const { isAuthenticated } = storeToRefs(useAuthStore());
    const { total } = storeToRefs(useOrderStore());
    const apiResponseCode = useApiResponse();

    return useQuery({
        queryKey: [QUERY_ORDERS, selectedLocationIds],
        queryFn: async (): Promise<GetOrderResponse[]> => {
            try {
                const response: AxiosResponse<GetOrderIdsResponse> = await orderApi.apiV1OrderPost({
                    locations: selectedLocationIds?.value,
                });

                if (apiResponseCode(response.status).ok) {
                    locationInfo.value = {
                        partnerCount: response.data?.partnerCount || 0,
                        orderCount: response.data?.orderIds?.length || 0,
                    };

                    if (total) {
                        total.value = response.data?.orderIds?.length || 0;
                    }

                    return response.data?.orderIds?.map(id => ({ order: { id } })) || [];
                } else {
                    throw new Error(
                        `Could not load order IDs with location IDs ${selectedLocationIds.value}. ${response.statusText}`
                    );
                }
            } catch (e) {
                console.error(e);
                throw e;
            }
        },
        enabled: computed(() => isAuthenticated.value && !!selectedLocationIds.value?.length),
        staleTime: ONE_MINUTE_IN_MS, // To have an up to date one minute order id list
    });
};

export const useGetProcessedOrdersList = (
    pageIndex?: Ref<number> | ComputedRef<number>,
    pageSize?: Ref<number> | ComputedRef<number>,
    queryEnabled: Ref<boolean> | ComputedRef<boolean> = ref(true),
    userIds?: Ref<string[]> | ComputedRef<string[]>,
    queryScope: 'list' | 'preview' | 'count' = 'list'
) => {
    const { isAuthenticated } = storeToRefs(useAuthStore());
    const { hasProcessedOrders } = storeToRefs(useOrderStore());
    const apiResponseCode = useApiResponse();
    const queryClient = useQueryClient();
    const toast = useLaunchToast();
    const { t } = useI18n();
    const { user } = storeToRefs(useAuthStore());

    const { ordersProcessedSortState: sortState, isDescending } = storeToRefs(useOrderStore());

    return useQuery({
        queryKey: [
            QUERY_PROCESSED_ORDERS,
            pageIndex,
            pageSize,
            sortState,
            userIds,
            ...(queryScope === 'list' ? [] : [queryScope]),
        ],
        queryFn: async (): Promise<ProcessedOrderResponsePagedListResponse> => {
            if (!user.value?.id || !userIds?.value?.length) {
                throw new Error('User ID is required');
            }
            try {
                const request: GetProcessedOrdersRequest = {
                    userIds: userIds?.value.length ? userIds?.value : [user.value.id],
                    processedOrderSorting:
                        sortState?.value.columnName === 'changedAt'
                            ? ProcessedOrderSorting.UpdatedDate
                            : ProcessedOrderSorting.ConstructionService,
                    pagination: {
                        isSortingDescending: isDescending.value,
                        pageIndex: pageIndex?.value ?? 0,
                        pageSize: pageSize?.value ?? DEFAULT_PAGE_SIZE,
                    },
                } satisfies GetProcessedOrdersRequest;
                if (!request.userIds?.length) {
                    const users = await queryClient.fetchQuery(getUsersQueryOptions());
                    request.userIds = getUserIds(users);
                }
                const response = await orderApi.apiV1OrderProcessedPost(request);

                if (apiResponseCode(response.status).ok) {
                    if (!userIds) {
                        hasProcessedOrders.value = !!response.data?.totalCount;
                    }
                    return response.data;
                } else {
                    throw new Error(`Could not load processed orders. ${response.statusText}`);
                }
            } catch (e) {
                toast('error', t('page.ordersProcessed.errors.loading.message'))();
                throw e;
            }
        },
        enabled: computed(() => isAuthenticated.value && queryEnabled.value && !!user.value?.id),
        staleTime: 0,
    });
};

export const useSetIsOrderConstructionService = () => {
    const orderStore = useOrderStore();
    const { data: currentOrder } = useGetOrder();
    const { resetCurrentOrder } = orderStore;
    const toast = useLaunchToast();
    const apiResponseCode = useApiResponse();
    const { t } = useI18n();
    const { selectedLocationIds } = storeToRefs(useLocationsStore());
    const queryClient = useQueryClient();

    return useMutation({
        mutationKey: [QUERY_SET_ORDER_IS_CONSTRUCTION_SERVICE],
        mutationFn: async (params: { isConstruction: boolean }) => {
            // Client ID is not required for request, but we still need to ensure it exists for validation purposes.
            if (!currentOrder.value?.order?.id || !currentOrder.value?.order?.clientId) {
                throw new Error(
                    'No processed order ID or client ID available to set construction-service decision for.'
                );
            }
            try {
                const request: ConstructionServiceRequest = {
                    orderId: currentOrder.value.order.id,
                    action: params.isConstruction ? 'Confirm' : 'Reject',
                };
                const response = await orderApi.apiV1OrderConstructionservicePut(request);
                if (!apiResponseCode(response.status).ok) {
                    throw new Error(
                        t('actions.errors.general', {
                            cause: response.statusText,
                        })
                    );
                }
                return response;
            } catch (e) {
                console.error(e);
                throw e;
            }
        },
        onSuccess: async (_, params) => {
            await invalidateQueryKeys(queryClient, [
                [QUERY_ORDER, currentOrder.value?.order?.id],
                [QUERY_ORDERS, selectedLocationIds],
                [QUERY_PROCESSED_ORDERS],
            ]);
            resetCurrentOrder();

            toast(
                'success',
                params.isConstruction ? t('actions.success.yes') : t('actions.success.no')
            )();
        },
        onError: error => {
            toast('error', error.message || t(UNKNOWN_ERROR_TRANSLATION_KEY))();
        },
    });
};

export const useSetClient = () => {
    const toast = useLaunchToast();
    const apiResponseCode = useApiResponse();
    const { t } = useI18n();
    const queryClient = useQueryClient();

    return useMutation({
        mutationKey: [QUERY_SET_ORDER_CLIENT],
        mutationFn: async (params: { clientId: number; orderId?: number }) => {
            // Client ID is required for request, so we need to ensure it exists.
            if (!params.orderId || !params.clientId) {
                throw new Error(
                    'No processed order ID or client ID available to set for the order.'
                );
            }
            try {
                const request: OrderSetClientRequest = {
                    orderId: params.orderId,
                    clientId: params.clientId,
                };
                const response = await orderApi.apiV1OrderClientPut(request);
                if (!apiResponseCode(response.status).ok) {
                    throw new Error(
                        t('actions.errors.general', {
                            cause: response.statusText,
                        })
                    );
                }
                return response;
            } catch (e) {
                console.error(e);
                throw e;
            }
        },
        onSuccess: async (_, params) => {
            await invalidateQueryKeys(queryClient, [
                [QUERY_ORDER, params.orderId],
                [QUERY_PROCESSED_ORDERS],
            ]);

            toast('success', t('actions.success.setClient'))();
        },
        onError: error => {
            toast('error', error.message || t(UNKNOWN_ERROR_TRANSLATION_KEY))();
        },
    });
};

export const useSetTransferOrders = () => {
    const apiResponseCode = useApiResponse();
    const toast = useLaunchToast();
    const { t } = useI18n();
    const queryClient = useQueryClient();
    const { showConfirmationDialog } = storeToRefs(useDialogStore());

    return useMutation({
        mutationKey: [QUERY_PROCESSED_ORDERS, QUERY_TRANSFER],
        mutationFn: async (userIds: string[]) => {
            if (userIds?.length === 0) {
                throw new Error(t('actions.errors.noUserIdToken'));
            }
            const request: ConstructionServiceTransferRequest = {
                userIds,
            };
            const response = await creditNoteApi.apiV1CreditnoteTransferPost(request);
            if (apiResponseCode(response.status).ok) {
                return response;
            } else {
                throw new Error(
                    t('actions.errors.transfer', {
                        cause: response.statusText,
                    })
                );
            }
        },
        onSuccess: async () => {
            await invalidateQueryKeys(queryClient, [
                [QUERY_PROCESSED_ORDERS],
                [QUERY_CREDIT_NOTE_LIST],
            ]);
            toast('success', t('actions.success.transfer'))();
            showConfirmationDialog.value = 0;
        },
        onError: error => {
            toast('error', error.message || t(UNKNOWN_ERROR_TRANSLATION_KEY))();
        },
    });
};
